import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

export default function ContactPage() {
  const [formState, setFormState] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const message = formData.get('message') as string;
    if (message && message.length > 500) {
      return;
    }
    const params = new URLSearchParams();
    formData.forEach((value, key) => {
      params.append(key, value.toString());
    });
    setFormState('submitting');
    try {
      const res = await fetch('https://readdy.ai/api/form/d82s0kcagmafep7uqmh0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      if (res.ok) {
        setFormState('success');
        form.reset();
        setCharCount(0);
      } else {
        setFormState('error');
      }
    } catch {
      setFormState('error');
    }
  };

  return (
    <div className="min-h-screen bg-firmai-dark">
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative pt-28 pb-14 overflow-hidden">
          <div className="absolute inset-0 opacity-10 pointer-events-none">
            <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(0,217,163,0.15) 1px, transparent 0)', backgroundSize: '40px 40px' }} />
          </div>
          <div className="w-full px-4 md:px-8 lg:px-12 relative z-10 text-center">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-firmai-green/10 border border-firmai-green/25 text-firmai-green text-xs font-semibold mb-5">
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-customer-service-2-line" /></span>
              专属顾问团队 · 快速响应
            </span>
            <h1 className="font-display font-bold text-3xl md:text-5xl text-white mb-4">联系我们</h1>
            <p className="text-firmai-text text-base md:text-lg max-w-2xl mx-auto">
              无论是工信部碳排申报咨询、工业AI项目评估还是CCER/CBAM合规服务，我们的专业顾问团队将在24小时内回复您
            </p>
          </div>
        </section>

        {/* Contact Content */}
        <section className="pb-20 bg-firmai-darker">
          <div className="w-full px-4 md:px-8 lg:px-12">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start max-w-6xl mx-auto">
              {/* Left - Info */}
              <div className="space-y-6">
                <div className="bg-firmai-card border border-firmai-border rounded-xl p-6">
                  <h2 className="text-white font-bold text-lg mb-5">联系方式</h2>
                  <div className="space-y-4">
                    {[
                      { icon: 'ri-phone-line', label: '服务热线', value: '18916035855', sub: '工作日 9:00-18:00' },
                      { icon: 'ri-mail-line', label: '商务邮箱', value: 'info@xunyutech.cn', sub: '24小时内回复' },
                      { icon: 'ri-map-pin-line', label: '公司地址', value: '上海市虹口区水电路1388号', sub: '' },
                      { icon: 'ri-time-line', label: '工作时间', value: '周一至周五 9:00-18:00', sub: '节假日紧急情况可拨打热线' },
                    ].map((item) => (
                      <div key={item.label} className="flex items-start gap-3">
                        <span className="w-9 h-9 flex items-center justify-center bg-firmai-green/10 rounded-lg text-firmai-green shrink-0">
                          <i className={item.icon} />
                        </span>
                        <div>
                          <div className="text-firmai-muted text-xs">{item.label}</div>
                          <div className="text-white text-sm font-medium mt-0.5">{item.value}</div>
                          <div className="text-firmai-muted text-xs mt-0.5">{item.sub}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-firmai-card border border-firmai-border rounded-xl p-6">
                  <h2 className="text-white font-bold text-lg mb-4">新媒体矩阵</h2>
                  <div className="grid grid-cols-3 gap-3">
                    {[
                      { icon: 'ri-wechat-fill', label: '微信公众号', sub: 'Firmai能碳智能' },
                      { icon: 'ri-video-line', label: '视频号', sub: 'Firmai工业AI' },
                      { icon: 'ri-wechat-2-fill', label: '企业微信', sub: '商务合作咨询' },
                    ].map((item) => (
                      <div key={item.label} className="bg-firmai-dark border border-firmai-border rounded-xl p-4 text-center cursor-pointer hover:border-firmai-green/30 transition-all duration-200">
                        <span className="w-10 h-10 flex items-center justify-center mx-auto text-firmai-green mb-2">
                          <i className={`${item.icon} text-2xl`} />
                        </span>
                        <div className="text-white text-xs font-medium">{item.label}</div>
                        <div className="text-firmai-muted text-xs mt-1">{item.sub}</div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-firmai-card border border-firmai-border rounded-xl p-6">
                  <h2 className="text-white font-bold text-lg mb-4">快速了解业务</h2>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      '工信部碳排申报', '工业AI智能化', 'CCER项目开发', 'CBAM出口合规',
                      '数字化能碳中心', '碳资产管理', 'ESG报告编制', '绿色工厂申报',
                    ].map((tag) => (
                      <div key={tag} className="flex items-center gap-2 text-xs text-firmai-text py-1.5 px-2 bg-firmai-dark border border-firmai-border rounded-lg">
                        <span className="w-3 h-3 flex items-center justify-center text-firmai-green">
                          <i className="ri-check-line" />
                        </span>
                        {tag}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Right - Form */}
              <div className="bg-firmai-card border border-firmai-border rounded-xl p-6 md:p-8">
                <h2 className="text-white font-bold text-lg mb-2">在线留言</h2>
                <p className="text-firmai-muted text-sm mb-6">填写信息，专属顾问将在24小时内与您联系</p>

                {formState === 'success' ? (
                  <div className="text-center py-12">
                    <span className="w-16 h-16 flex items-center justify-center mx-auto bg-firmai-green/10 rounded-full text-firmai-green mb-4">
                      <i className="ri-check-double-line text-3xl" />
                    </span>
                    <div className="text-white font-semibold text-lg mb-2">留言提交成功！</div>
                    <p className="text-firmai-muted text-sm">专属顾问将在24小时内与您取得联系，感谢您的信任！</p>
                    <button
                      onClick={() => setFormState('idle')}
                      className="mt-6 px-6 py-2.5 bg-firmai-green text-firmai-dark font-semibold rounded-md hover:bg-firmai-green/90 transition-all duration-200 cursor-pointer"
                    >
                      继续留言
                    </button>
                  </div>
                ) : (
                  <form
                    data-readdy-form
                    onSubmit={handleSubmit}
                    className="space-y-4"
                  >
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-firmai-muted text-xs mb-1.5">姓名 <span className="text-red-400">*</span></label>
                        <input
                          type="text"
                          name="name"
                          required
                          placeholder="您的姓名"
                          className="w-full bg-firmai-dark border border-firmai-border rounded-lg px-3 py-2.5 text-white text-sm placeholder-firmai-muted focus:outline-none focus:border-firmai-green/50 transition-colors"
                        />
                      </div>
                      <div>
                        <label className="block text-firmai-muted text-xs mb-1.5">公司名称 <span className="text-red-400">*</span></label>
                        <input
                          type="text"
                          name="company"
                          required
                          placeholder="所在公司"
                          className="w-full bg-firmai-dark border border-firmai-border rounded-lg px-3 py-2.5 text-white text-sm placeholder-firmai-muted focus:outline-none focus:border-firmai-green/50 transition-colors"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-firmai-muted text-xs mb-1.5">联系电话 <span className="text-red-400">*</span></label>
                        <input
                          type="tel"
                          name="phone"
                          required
                          placeholder="手机号码"
                          className="w-full bg-firmai-dark border border-firmai-border rounded-lg px-3 py-2.5 text-white text-sm placeholder-firmai-muted focus:outline-none focus:border-firmai-green/50 transition-colors"
                        />
                      </div>
                      <div>
                        <label className="block text-firmai-muted text-xs mb-1.5">邮箱地址</label>
                        <input
                          type="email"
                          name="email"
                          placeholder="email@company.com"
                          className="w-full bg-firmai-dark border border-firmai-border rounded-lg px-3 py-2.5 text-white text-sm placeholder-firmai-muted focus:outline-none focus:border-firmai-green/50 transition-colors"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-firmai-muted text-xs mb-1.5">咨询业务 <span className="text-red-400">*</span></label>
                      <select
                        name="service_type"
                        required
                        className="w-full bg-firmai-dark border border-firmai-border rounded-lg px-3 py-2.5 text-sm text-white focus:outline-none focus:border-firmai-green/50 transition-colors"
                      >
                        <option value="">请选择咨询业务</option>
                        <option value="工信部碳排放申报服务">工信部碳排放申报服务</option>
                        <option value="工业AI智能化解决方案">工业AI智能化解决方案</option>
                        <option value="CCER项目开发与数据上报">CCER项目开发与数据上报</option>
                        <option value="CBAM跨境碳合规填报">CBAM跨境碳合规填报</option>
                        <option value="数字化能碳管理中心建设">数字化能碳管理中心建设</option>
                        <option value="ESG报告编制">ESG报告编制</option>
                        <option value="其他咨询">其他咨询</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-firmai-muted text-xs mb-1.5">行业类型</label>
                      <select
                        name="industry"
                        className="w-full bg-firmai-dark border border-firmai-border rounded-lg px-3 py-2.5 text-sm text-white focus:outline-none focus:border-firmai-green/50 transition-colors"
                      >
                        <option value="">请选择所在行业</option>
                        <option value="钢铁冶金">钢铁冶金</option>
                        <option value="水泥建材">水泥建材</option>
                        <option value="化工行业">化工行业</option>
                        <option value="电力能源">电力能源</option>
                        <option value="装备制造">装备制造</option>
                        <option value="外贸出口制造">外贸出口制造</option>
                        <option value="工业园区">工业园区</option>
                        <option value="其他行业">其他行业</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-firmai-muted text-xs mb-1.5">
                        留言内容
                        <span className={`ml-2 ${charCount > 450 ? 'text-red-400' : 'text-firmai-muted'}`}>{charCount}/500</span>
                      </label>
                      <textarea
                        name="message"
                        rows={4}
                        maxLength={500}
                        placeholder="请描述您的具体需求或问题，我们将为您提供针对性的解决方案..."
                        onChange={(e) => setCharCount(e.target.value.length)}
                        className="w-full bg-firmai-dark border border-firmai-border rounded-lg px-3 py-2.5 text-white text-sm placeholder-firmai-muted focus:outline-none focus:border-firmai-green/50 transition-colors resize-none"
                      />
                    </div>

                    {formState === 'error' && (
                      <div className="text-red-400 text-sm bg-red-400/10 border border-red-400/30 rounded-lg px-4 py-3">
                        提交失败，请稍后重试或直接拨打18916035855
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={formState === 'submitting'}
                      className="w-full py-3 bg-firmai-green text-firmai-dark font-semibold rounded-md hover:bg-firmai-green/90 transition-all duration-200 cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap"
                    >
                      {formState === 'submitting' ? '提交中...' : '提交留言'}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}